<template>
    <div class="min-h-screen flex flex-col lg:flex-row bg-secondary-50 dark:bg-secondary-900">
        <!-- Left Panel - Branding & Marketing (Hidden on mobile) -->
        <div class="hidden lg:flex lg:flex-1 relative overflow-hidden bg-gradient-to-br from-primary-600 via-primary-700 to-primary-900">
            <!-- Decorative background elements -->
            <div class="absolute inset-0 opacity-10">
                <div class="absolute top-0 -left-4 w-72 h-72 bg-white rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
                <div class="absolute top-0 -right-4 w-72 h-72 bg-white rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
                <div class="absolute -bottom-8 left-20 w-72 h-72 bg-white rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
            </div>

            <!-- Content -->
            <div class="relative z-10 flex flex-col justify-between p-12 text-white">
                <!-- Logo and Tagline -->
                <div class="space-y-6">
                    <!-- Logo -->
                    <div class="flex items-center space-x-3">
                        <div class="w-12 h-12 bg-white rounded-xl flex items-center justify-center">
                            <svg class="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                        </div>
                        <span class="text-2xl font-bold">{{ appName }}</span>
                    </div>

                    <!-- Tagline -->
                    <div class="space-y-2">
                        <h1 class="text-4xl font-bold leading-tight">
                            Complete Resolution Management Platform
                        </h1>
                        <p class="text-xl text-primary-100">
                            Streamline every resolution from IT tickets to maintenance requests and incident reports — all in one place
                        </p>
                    </div>
                </div>

                <!-- Features List -->
                <div class="space-y-6 my-12">
                    <div
                        v-for="feature in features"
                        :key="feature.title"
                        class="flex items-start space-x-4 group"
                    >
                        <div class="flex-shrink-0 w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center group-hover:bg-white/20 transition-colors">
                            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <div>
                            <h3 class="font-semibold text-lg">{{ feature.title }}</h3>
                            <p class="text-primary-100">{{ feature.description }}</p>
                        </div>
                    </div>
                </div>

                <!-- Testimonial Carousel -->
                <div class="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 relative">
                    <!-- Navigation Arrows -->
                    <button
                        @click="previousTestimonial"
                        class="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors z-10"
                        aria-label="Previous testimonial"
                    >
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <button
                        @click="nextTestimonial"
                        class="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors z-10"
                        aria-label="Next testimonial"
                    >
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                        </svg>
                    </button>

                    <!-- Content Container with padding for arrows -->
                    <div class="px-12">
                        <!-- Stars -->
                        <div class="flex items-center space-x-1 mb-3">
                            <svg v-for="i in 5" :key="i" class="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                        </div>

                        <!-- Testimonial Content with Transition -->
                        <transition name="testimonial-fade" mode="out-in">
                            <div :key="currentTestimonialIndex">
                                <p class="text-white mb-1 italic min-h-24">
                                    "{{ testimonials[currentTestimonialIndex].quote }}"
                                </p>
                                <div class="flex items-center space-x-3">
                                    <div class="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center font-semibold">
                                        {{ testimonials[currentTestimonialIndex].initials }}
                                    </div>
                                    <div>
                                        <p class="font-semibold">{{ testimonials[currentTestimonialIndex].author }}</p>
                                        <p class="text-sm text-primary-100">{{ testimonials[currentTestimonialIndex].role }}</p>
                                    </div>
                                </div>
                            </div>
                        </transition>
                    </div>

                    <!-- Dots Indicator -->
                    <div class="flex justify-center space-x-2 mt-4">
                        <button
                            v-for="(testimonial, index) in testimonials"
                            :key="index"
                            @click="goToTestimonial(index)"
                            :class="[
                                'w-2 h-2 rounded-full transition-all',
                                currentTestimonialIndex === index
                                    ? 'bg-white w-6'
                                    : 'bg-white/40 hover:bg-white/60'
                            ]"
                            :aria-label="`Go to testimonial ${index + 1}`"
                        ></button>
                    </div>
                </div>

                <!-- Footer -->
                <div class="text-primary-100 text-sm mt-8">
                    © {{ currentYear }} {{ appName }}. All rights reserved.
                </div>
            </div>
        </div>

        <!-- Right Panel - Form Content -->
        <div class="flex-1 flex flex-col min-h-screen lg:min-h-0">
            <!-- Mobile Header -->
            <div class="lg:hidden bg-white dark:bg-secondary-800 border-b border-secondary-200 dark:border-secondary-700 p-4">
                <div class="flex items-center space-x-3">
                    <div class="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center">
                        <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                    </div>
                    <span class="text-xl font-bold text-secondary-900 dark:text-white">{{ appName }}</span>
                </div>
            </div>

            <!-- Form Container -->
            <div class="flex-1 flex items-center justify-center p-6 sm:p-8 lg:p-12">
                <div class="w-full max-w-2xl">
                    <!-- Dark Mode Toggle -->
                    <div class="flex justify-end mb-6">
                        <button
                            @click="toggleDarkMode"
                            type="button"
                            class="p-2 text-secondary-500 hover:text-secondary-700 dark:text-secondary-400 dark:hover:text-secondary-200 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors"
                            aria-label="Toggle dark mode"
                        >
                            <!-- Sun icon (show in dark mode) -->
                            <svg v-if="isDark" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                            <!-- Moon icon (show in light mode) -->
                            <svg v-else class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                            </svg>
                        </button>
                    </div>

                    <!-- Form Content Slot -->
                    <div class="bg-white dark:bg-secondary-800 rounded-2xl shadow-xl border border-secondary-200 dark:border-secondary-700 p-8">
                        <slot />
                    </div>

                    <!-- Additional Links Slot -->
                    <div v-if="$slots.footer" class="mt-6 text-center">
                        <slot name="footer" />
                    </div>

                    <!-- Default Footer Links (if no custom footer) -->
                    <div v-else class="mt-6 text-center space-y-4">
                        <p class="text-sm text-secondary-600 dark:text-secondary-400">
                            <slot name="toggle-link">
                                <!-- Default toggle link will be provided by pages -->
                            </slot>
                        </p>

                        <div class="flex items-center justify-center space-x-4 text-sm">
                            <a href="/help" class="text-secondary-600 dark:text-secondary-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                                Help Center
                            </a>
                            <span class="text-secondary-300 dark:text-secondary-600">•</span>
                            <a href="/privacy" class="text-secondary-600 dark:text-secondary-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                                Privacy
                            </a>
                            <span class="text-secondary-300 dark:text-secondary-600">•</span>
                            <a href="/terms" class="text-secondary-600 dark:text-secondary-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                                Terms
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Toast Notifications -->
        <Toast />
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { usePage } from '@inertiajs/vue3';
import Toast from '@/Components/Common/Toast.vue';

// Get app name from page props or use default
const page = usePage();
const appName = computed(() => page.props.app?.name || 'Margin Resolve');

// Current year for footer
const currentYear = new Date().getFullYear();

// Dark mode state
const isDark = ref(false);

// Features list
const features = [
    {
        title: 'IT Helpdesk Ticketing',
        description: 'Manage IT support tickets, track resolution times, and provide seamless technical support',
    },
    {
        title: 'Maintenance Work Orders',
        description: 'Create, assign, and track maintenance requests from submission to completion',
    },
    {
        title: 'Incident Management',
        description: 'Document workplace incidents, conduct investigations, and implement corrective actions',
    },
    {
        title: 'Advanced Reporting & Analytics',
        description: 'Generate comprehensive reports across all modules with real-time performance insights',
    },
];

// Testimonials
const testimonials = [
    {
        quote: 'Margin Resolve unified our IT helpdesk, maintenance, and incident tracking. We reduced response times by 40% and improved team collaboration significantly.',
        author: 'Sarah Johnson',
        role: 'IT Director at TechCorp',
        initials: 'SJ',
    },
    {
        quote: 'The maintenance work order system transformed our operations. We can now track every repair request from submission to completion with complete transparency.',
        author: 'Michael Chen',
        role: 'Facilities Manager at GlobalCorp',
        initials: 'MC',
    },
    {
        quote: 'The incident reporting module is outstanding. We can document safety incidents, assign investigations, and track corrective actions all in one place.',
        author: 'Jennifer Martinez',
        role: 'Safety Coordinator at IndustrialTech',
        initials: 'JM',
    },
    {
        quote: 'Finally, a platform that handles everything - from simple IT tickets to complex maintenance workflows. The ROI was evident within the first month.',
        author: 'David Thompson',
        role: 'Operations Director at ManufacturePlus',
        initials: 'DT',
    },
    {
        quote: 'Real-time notifications and comprehensive reporting give us complete visibility. Management loves the insights, and our team loves how easy it is to use.',
        author: 'Emily Rodriguez',
        role: 'Service Desk Manager at HealthCare Systems',
        initials: 'ER',
    },
];

// Current testimonial index for carousel
const currentTestimonialIndex = ref(0);

// Auto-rotate testimonials every 3 seconds
let testimonialInterval = null;

// Dark mode toggle
const toggleDarkMode = () => {
    isDark.value = !isDark.value;

    if (isDark.value) {
        document.documentElement.classList.add('dark');
        localStorage.setItem('theme', 'dark');
    } else {
        document.documentElement.classList.remove('dark');
        localStorage.setItem('theme', 'light');
    }
};

// Navigate testimonials
const nextTestimonial = () => {
    currentTestimonialIndex.value = (currentTestimonialIndex.value + 1) % testimonials.length;
};

const previousTestimonial = () => {
    currentTestimonialIndex.value = (currentTestimonialIndex.value - 1 + testimonials.length) % testimonials.length;
};

const goToTestimonial = (index) => {
    currentTestimonialIndex.value = index;
};

// Initialize dark mode from localStorage or system preference
onMounted(() => {
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;

    if (savedTheme === 'dark' || (!savedTheme && prefersDark)) {
        isDark.value = true;
        document.documentElement.classList.add('dark');
    }

    // Clear the logout flag if it exists
    // This ensures the flag is cleaned up when user lands on login page after logout
    if (sessionStorage.getItem('is_logging_out')) {
        sessionStorage.removeItem('is_logging_out');
    }

    // Start testimonial carousel
    testimonialInterval = setInterval(() => {
        nextTestimonial();
    }, 5000); // Change every 5 seconds
});

// Cleanup interval on unmount
onUnmounted(() => {
    if (testimonialInterval) {
        clearInterval(testimonialInterval);
    }
});
</script>

<style scoped>
/* Blob animation */
@keyframes blob {
    0% {
        transform: translate(0px, 0px) scale(1);
    }
    33% {
        transform: translate(30px, -50px) scale(1.1);
    }
    66% {
        transform: translate(-20px, 20px) scale(0.9);
    }
    100% {
        transform: translate(0px, 0px) scale(1);
    }
}

.animate-blob {
    animation: blob 7s infinite;
}

.animation-delay-2000 {
    animation-delay: 2s;
}

.animation-delay-4000 {
    animation-delay: 4s;
}

/* Smooth transitions */
* {
    transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 150ms;
}

/* Testimonial fade transition */
.testimonial-fade-enter-active,
.testimonial-fade-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
}

.testimonial-fade-enter-from {
    opacity: 0;
    transform: translateX(20px);
}

.testimonial-fade-leave-to {
    opacity: 0;
    transform: translateX(-20px);
}
</style>
